import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loading, Calendar, DivisionsList, TournamentMaps } from "../components";
import {
  current_tournament,
  api_v3_get_tournament_by_id,
  api_v3_get_tournament_details,
} from "../utils/constants";

const formatDate = (str) => {
  if (!str) return "";
  try {
    const d = new Date(str);
    const m = ["ENE","FEB","MAR","ABR","MAY","JUN","JUL","AGO","SEP","OCT","NOV","DEC"];
    return `${d.getDate()} ${m[d.getMonth()]} ${d.getFullYear()}`;
  } catch { return str; }
};

const TournamentInfo = () => {
  const { tournamentId } = useParams();
  const id = tournamentId || current_tournament;

  const [tournament, setTournament] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      axios.get(api_v3_get_tournament_by_id(id)),
      axios.get(api_v3_get_tournament_details(id)).catch(() => null),
    ])
      .then(([tRes, dRes]) => {
        const t = tRes.data;
        setTournament(t);
        if (t.divisions) setDivisions(t.divisions.sort((a, b) => a.sequence - b.sequence));
        if (dRes?.status === 200) setDetails(dRes.data);
      })
      .catch((err) => { console.error("Error loading tournament:", err); setError(true); })
      .finally(() => setLoading(false));
  }, [id]);

  const registersOpen = tournament?.status === "registers";
  const prizeDesc = details?.tournamentDescriptions?.find((d) => d.title === "Premios");

  if (loading) return <Canvas><Loading /></Canvas>;
  if (error) return <Canvas><Empty>Error al cargar la información del torneo.</Empty></Canvas>;
  if (!tournament) return <Canvas><Empty>Torneo no encontrado.</Empty></Canvas>;

  return (
    <Canvas>
      <Content>
        <BackLink to="/">← VOLVER</BackLink>

        <HeroBlock>
          {details?.image && <Logo src={details.image} alt={tournament.name} />}
          <Title>{tournament.name}</Title>
          <HeroMeta>
            <StatusPill $status={tournament.status}>{tournament.status}</StatusPill>
            {tournament.startDate && (
              <DateRange>{formatDate(tournament.startDate)} — {formatDate(tournament.endDate)}</DateRange>
            )}
          </HeroMeta>
        </HeroBlock>

        {registersOpen && (
          <Card>
            <CardTitle>INSCRÍBETE</CardTitle>
            <CTARow>
              <PrimaryBtn to={`/register/tournament/${id}`}>APUNTARTE</PrimaryBtn>
              <SecLink to={`/registers/${id}`}>VER INSCRIPTOS →</SecLink>
            </CTARow>
          </Card>
        )}

        {tournament.milestones?.length > 0 && (
          <Card>
            <CardTitle>CALENDARIO</CardTitle>
            <Calendar events={tournament.milestones} filterEvents={false} />
          </Card>
        )}

        {divisions.length > 0 && (
          <Card>
            <CardTitle>DIVISIONES</CardTitle>
            <DivisionsList divisions={divisions} />
          </Card>
        )}

        {(details?.maps?.length > 0 || details?.drafts) && (
          <Card>
            <CardTitle>MAPAS & DRAFTS</CardTitle>
            {details.maps?.length > 0 && <TournamentMaps maps={details.maps} />}
            {details.drafts && Object.keys(details.drafts).length > 0 && (
              <DraftGrid>
                {Object.entries(details.drafts).map(([key, draft]) => (
                  <React.Fragment key={key}>
                    {draft.mapLink && <DraftBtn href={draft.mapLink} target="_blank" rel="noopener noreferrer">MAPAS {key.replace("draft_", "")}</DraftBtn>}
                    {draft.civLink && <DraftBtn href={draft.civLink} target="_blank" rel="noopener noreferrer">CIVS {key.replace("draft_", "")}</DraftBtn>}
                  </React.Fragment>
                ))}
              </DraftGrid>
            )}
          </Card>
        )}

        {details?.handbookLink && (
          <Card>
            <CardTitle>REGLAMENTO</CardTitle>
            <HandbookBtn href={details.handbookLink} target="_blank" rel="noopener noreferrer">
              HANDBOOK {tournament.name}
            </HandbookBtn>
          </Card>
        )}

        {prizeDesc && (
          <Card>
            <CardTitle>PREMIOS</CardTitle>
            <PrizesHTML dangerouslySetInnerHTML={{ __html: prizeDesc.text }} />
          </Card>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`background: var(--v-canvas); min-height: 100vh; display: flex; justify-content: center; padding: 4rem 1.5rem;`;
const Content = styled.div`width: 100%; max-width: 900px;`;
const Empty = styled.p`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg-muted); text-align: center; padding: 4rem 1rem;`;

const BackLink = styled(Link)`display: inline-flex; align-items: center; gap: 0.4rem; font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-mint); text-decoration: none; padding: 0.5rem 0.9rem; border: 1px solid rgba(60,255,208,0.2); border-radius: 24px; transition: background 0.15s ease; margin-bottom: 2rem; &:hover { background: rgba(60,255,208,0.04); }`;

const HeroBlock = styled.div`display: flex; flex-direction: column; align-items: center; text-align: center; margin-bottom: 2.5rem;`;
const Logo = styled.img`max-width: 240px; margin-bottom: 1.5rem; border-radius: 20px;`;
const Title = styled.h1`font-family: Anton, sans-serif; font-size: clamp(48px, 8vw, 72px); font-weight: 400; text-transform: uppercase; line-height: 1; color: var(--v-fg); margin: 0 0 0.75rem;`;
const HeroMeta = styled.div`display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; justify-content: center;`;

const StatusPill = styled.span`font-family: var(--v-font-mono); font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.25rem 0.65rem; border-radius: 12px; background: ${({ $status }) =>
  $status === "registers" || $status === "on going" ? "rgba(60,255,208,0.12)" :
  $status === "playoffs" || $status === "registers-closed" ? "rgba(201,160,78,0.12)" :
  $status === "finished" ? "rgba(255,255,255,0.04)" :
  "rgba(82,0,255,0.12)"};
color: ${({ $status }) =>
  $status === "registers" || $status === "on going" ? "var(--v-mint)" :
  $status === "playoffs" || $status === "registers-closed" ? "var(--v-gold-bright, #c9a04e)" :
  $status === "finished" ? "var(--v-fg-muted)" :
  "var(--v-uv, #5200ff)"};
border: 1px solid ${({ $status }) =>
  $status === "registers" || $status === "on going" ? "rgba(60,255,208,0.3)" :
  $status === "playoffs" || $status === "registers-closed" ? "rgba(201,160,78,0.3)" :
  $status === "finished" ? "rgba(255,255,255,0.08)" :
  "rgba(82,0,255,0.3)"};`;

const DateRange = styled.span`font-family: var(--v-font-mono); font-size: 10px; color: var(--v-fg-muted); letter-spacing: 0.3px;`;

const Card = styled.div`background: var(--v-surface); border: 1px solid rgba(255,255,255,0.06); border-radius: 20px; padding: 1.5rem; margin-bottom: 1.25rem;`;
const CardTitle = styled.h3`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: var(--v-fg-muted); margin: 0 0 1rem;`;

const CTARow = styled.div`display: flex; align-items: center; gap: 1rem; flex-wrap: wrap;`;
const PrimaryBtn = styled(Link)`display: inline-flex; align-items: center; justify-content: center; background: var(--v-mint); color: #000; font-family: var(--v-font-mono); font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; padding: 0.6rem 1.5rem; border-radius: 24px; text-decoration: none; transition: opacity 0.15s ease; &:hover { opacity: 0.85; }`;
const SecLink = styled(Link)`font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; color: var(--v-fg-muted); text-decoration: none; transition: color 0.15s ease; &:hover { color: var(--v-fg); }`;

const DraftGrid = styled.div`display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.75rem;`;
const DraftBtn = styled.a`display: inline-flex; align-items: center; padding: 0.4rem 0.9rem; background: rgba(60,255,208,0.06); border: 1px solid rgba(60,255,208,0.2); border-radius: 20px; color: var(--v-mint); font-family: var(--v-font-mono); font-size: 9px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; text-decoration: none; transition: background 0.15s ease; &:hover { background: rgba(60,255,208,0.12); }`;

const HandbookBtn = styled.a`display: inline-flex; align-items: center; padding: 0.5rem 1.25rem; background: transparent; border: 1px solid rgba(255,255,255,0.15); border-radius: 24px; color: var(--v-fg); font-family: var(--v-font-mono); font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; text-decoration: none; transition: border-color 0.15s ease; &:hover { border-color: var(--v-mint); }`;

const PrizesHTML = styled.div`font-family: var(--v-font-body); font-size: 14px; color: var(--v-fg); line-height: 1.6; p, ul, li { color: var(--v-fg); margin: 0 0 0.5rem; } ul { padding-left: 1.5rem; } a { color: var(--v-mint); }`;

export default TournamentInfo;
